import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { ChevronRight, ArrowLeft, Save, Trash2, Plus, X, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  name: '', type_financeur: 'OPCO', email: '', code_interne: '',
  siret: '', langue: 'Français', adresse: '', code_postal: '',
  ville: '', pays: 'France FR', telephone: '', fax: '',
  numero_compte_comptabilite: '', notes: '', contacts: [],
};

const TABS = [
  { id: 'informations', label: 'Informations' },
  { id: 'contacts', label: 'Contacts' },
];

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full border-0 border-b border-slate-300 rounded-none bg-slate-50/50 px-3 py-2.5 text-sm focus:ring-0 focus:border-violet-500 outline-none transition-colors";
const selectCls = "w-full border-0 border-b border-slate-300 rounded-none bg-slate-50/50 px-3 py-2.5 text-sm focus:ring-0 focus:border-violet-500 outline-none appearance-none";

const FinanceurDetailPage = () => {
  const { financeurId } = useParams();
  const navigate = useNavigate();
  const isNew = !financeurId || financeurId === 'nouveau';
  const [activeTab, setActiveTab] = useState('informations');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const fetchFinanceur = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await fetch(`${API}/donnees-financeurs/${financeurId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({ ...prev, ...data, contacts: data.contacts || [] }));
      } else {
        toast.error("Financeur non trouvé");
        navigate('/donnees/financeurs');
      }
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [financeurId, isNew, navigate]);

  useEffect(() => { fetchFinanceur(); }, [fetchFinanceur]);

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error("Le nom est obligatoire"); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/donnees-financeurs` : `${API}/donnees-financeurs/${financeurId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form), credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(isNew ? 'Financeur créé' : 'Financeur mis à jour');
        if (isNew && data.financeur_id) navigate(`/donnees/financeurs/${data.financeur_id}`, { replace: true });
      } else { toast.error("Erreur lors de l'enregistrement"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer ce financeur ?")) return;
    await fetch(`${API}/donnees-financeurs/${financeurId}`, { method: 'DELETE', credentials: 'include' });
    toast.success("Financeur supprimé");
    navigate('/donnees/financeurs');
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const addContact = () => set('contacts', [...form.contacts, { nom: '', prenom: '', email: '', telephone: '', fonction: '' }]);
  const removeContact = (i) => set('contacts', form.contacts.filter((_, idx) => idx !== i));
  const updateContact = (i, key, val) => {
    const updated = [...form.contacts];
    updated[i] = { ...updated[i], [key]: val };
    set('contacts', updated);
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <div className="h-8 w-48 bg-slate-100 rounded animate-pulse" />
          <div className="h-96 bg-slate-100 rounded-xl animate-pulse" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5" data-testid="financeur-detail-page">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>Données</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="cursor-pointer hover:text-violet-500 transition-colors" onClick={() => navigate('/donnees/financeurs')}>Financeurs externes</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-600 font-medium">{isNew ? 'Nouveau financeur externe' : form.name}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/donnees/financeurs')} className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </button>
            <h1 className="text-2xl font-black text-slate-900" data-testid="page-title">
              {isNew ? 'Nouveau financeur externe' : form.name}
            </h1>
          </div>
          {!isNew && (
            <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-500 border-red-200 hover:bg-red-50" data-testid="delete-btn">
              <Trash2 className="h-4 w-4 mr-1.5" /> Supprimer
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex bg-violet-50 rounded-xl overflow-hidden" data-testid="financeur-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3.5 text-sm font-medium text-center transition-all ${
                activeTab === tab.id ? 'bg-violet-100 text-violet-800' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-btn">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>

        {/* Tab Content */}
        {activeTab === 'informations' && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="tab-content-informations">
            <h2 className="text-lg font-bold text-slate-900 mb-6">Informations générales</h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-5">
              <Field label="Nom *">
                <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} data-testid="input-name" />
              </Field>
              <Field label="Type *">
                <select className={selectCls} value={form.type_financeur} onChange={e => set('type_financeur', e.target.value)} data-testid="input-type">
                  <option value="OPCO">OPCO</option>
                  <option value="Pôle Emploi">Pôle Emploi</option>
                  <option value="Collectivité">Collectivité</option>
                  <option value="Région">Région</option>
                  <option value="État">État</option>
                  <option value="Autre">Autre</option>
                </select>
              </Field>
              <Field label="Email">
                <input className={inputCls} type="email" value={form.email} onChange={e => set('email', e.target.value)} data-testid="input-email" />
              </Field>
              <Field label="Code interne">
                <input className={inputCls} value={form.code_interne} onChange={e => set('code_interne', e.target.value)} data-testid="input-code-interne" />
              </Field>
              <Field label="N° SIRET">
                <input className={inputCls} value={form.siret} onChange={e => set('siret', e.target.value)} data-testid="input-siret" />
              </Field>
              <Field label="Langue">
                <select className={selectCls} value={form.langue} onChange={e => set('langue', e.target.value)} data-testid="input-langue">
                  <option value="Français">Français</option>
                  <option value="English">English</option>
                </select>
              </Field>
              <Field label="Adresse">
                <input className={inputCls} value={form.adresse} onChange={e => set('adresse', e.target.value)} data-testid="input-adresse" />
              </Field>
              <Field label="Code postal">
                <input className={inputCls} value={form.code_postal} onChange={e => set('code_postal', e.target.value)} data-testid="input-code-postal" />
              </Field>
              <Field label="Ville">
                <input className={inputCls} value={form.ville} onChange={e => set('ville', e.target.value)} data-testid="input-ville" />
              </Field>
              <Field label="Pays">
                <select className={selectCls} value={form.pays} onChange={e => set('pays', e.target.value)} data-testid="input-pays">
                  <option value="France FR">France FR</option>
                  <option value="CM">Cameroun CM</option>
                  <option value="BE">Belgique BE</option>
                  <option value="CH">Suisse CH</option>
                </select>
              </Field>
              <Field label="Téléphone">
                <input className={inputCls} value={form.telephone} onChange={e => set('telephone', e.target.value)} data-testid="input-telephone" />
              </Field>
              <Field label="Fax">
                <input className={inputCls} value={form.fax} onChange={e => set('fax', e.target.value)} data-testid="input-fax" />
              </Field>
              <Field label="N° de compte client en comptabilité">
                <input className={inputCls} value={form.numero_compte_comptabilite} onChange={e => set('numero_compte_comptabilite', e.target.value)} data-testid="input-numero-compte" />
              </Field>
              <Field label="Notes libres" className="col-span-2">
                <textarea className="w-full border-0 border-b border-slate-300 rounded-none bg-slate-50/50 px-3 py-2.5 text-sm focus:ring-0 focus:border-violet-500 outline-none min-h-[80px]" value={form.notes} onChange={e => set('notes', e.target.value)} data-testid="input-notes" />
              </Field>
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="tab-content-contacts">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-900">Contacts</h2>
              <Button onClick={addContact} variant="outline" size="sm" className="text-violet-600 border-violet-200 hover:bg-violet-50" data-testid="add-contact-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Ajouter un contact
              </Button>
            </div>
            {form.contacts.length === 0 ? (
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center">
                <Users className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500 mb-4">Aucun contact ajouté pour ce financeur</p>
                <Button onClick={addContact} variant="outline" size="sm" className="text-violet-600 border-violet-200 hover:bg-violet-50" data-testid="add-first-contact-btn">
                  <Plus className="h-4 w-4 mr-1.5" /> Ajouter le premier contact
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {form.contacts.map((contact, i) => (
                  <div key={i} className="border border-slate-200 rounded-xl p-5 relative group" data-testid={`contact-card-${i}`}>
                    <button onClick={() => removeContact(i)} className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-contact-${i}`}>
                      <X className="h-4 w-4" />
                    </button>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                        <span className="text-xs font-bold text-violet-600">{i + 1}</span>
                      </div>
                      <span className="text-sm font-semibold text-slate-700">Contact {i + 1}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <Field label="Nom">
                        <input className={inputCls} value={contact.nom} onChange={e => updateContact(i, 'nom', e.target.value)} data-testid={`contact-${i}-nom`} />
                      </Field>
                      <Field label="Prénom">
                        <input className={inputCls} value={contact.prenom} onChange={e => updateContact(i, 'prenom', e.target.value)} data-testid={`contact-${i}-prenom`} />
                      </Field>
                      <Field label="Fonction">
                        <input className={inputCls} value={contact.fonction} onChange={e => updateContact(i, 'fonction', e.target.value)} data-testid={`contact-${i}-fonction`} />
                      </Field>
                      <Field label="Email">
                        <input className={inputCls} value={contact.email} onChange={e => updateContact(i, 'email', e.target.value)} data-testid={`contact-${i}-email`} />
                      </Field>
                      <Field label="Téléphone">
                        <input className={inputCls} value={contact.telephone} onChange={e => updateContact(i, 'telephone', e.target.value)} data-testid={`contact-${i}-telephone`} />
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FinanceurDetailPage;
